import React, { useEffect, useMemo, useRef } from 'react';
import { getGravityUserStatus } from '../../hooks/useAuth';
import { useEntitlements } from '../../hooks/useEntitlements';
import type { AccessDescriptor } from '../../domains/access/models/AccessDescriptor';
import {
  getCommercialUpgradePresentation,
  type CommercialUpgradePresentation,
} from '../../domains/access/services/CommercialUpgradePresentation';
import { OPEN_ONBOARDING_EVENT, Pages } from '../modals/OnboardingDialog';
import { markUpgradeEntitlementRefreshPending, useUpgradeEntitlementRefresh } from '../../hooks/useUpgradeEntitlementRefresh';

type UpgradeDialogType =
  | 'export'
  | 'sharing.invite_by_email'
  | 'shapes.unlimited'
  | 'generic'
  | 'ai-templates'
  | 'premium-templates'
  | 'create-folder'
  | 'archived-folder'
  | 'create-workspace';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  page: UpgradeDialogType;
  baseUrl?: string;
  locationId?: string;
  secondary?: 'disabled' | 'hidden';
  errorMessage?: string;
  integration?: 'msteams' | 'confluence' | 'none';
  templateCategory?: string;
  shapeLibraryId?: string;
  shapeDefId?: string;
  onUpgrade?: () => void;
  onAction?: () => void;
  feature?: string;
  reason?: string;
  descriptor?: AccessDescriptor;
}

export function UpgradeModal({
  isOpen,
  onClose,
  onUpgrade,
  page,
  feature,
  reason,
  descriptor,
}: UpgradeModalProps) {
  const primaryActionRef = useRef<HTMLAnchorElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const priorFocusRef = useRef<HTMLElement | null>(null);
  const { entitlements } = useEntitlements();
  useUpgradeEntitlementRefresh();
  const presentation = useMemo(() => getCommercialUpgradePresentation({
    currentPlan: descriptor?.currentPlan ?? entitlements?.planType,
    feature: feature ?? page,
    reason,
    descriptor,
  }), [descriptor, entitlements?.planType, feature, page, reason]);

  useEffect(() => {
    if (!isOpen) return;
    priorFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const frame = window.requestAnimationFrame(() => primaryActionRef.current?.focus());
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab') return;
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href]');
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('keydown', handleKeyDown);
      priorFocusRef.current?.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleUpgrade = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const status = typeof getGravityUserStatus === 'function' ? getGravityUserStatus() : null;
    if (status === 'DEMO') {
      event.preventDefault();
      onUpgrade?.();
      onClose();
      window.dispatchEvent(new CustomEvent(OPEN_ONBOARDING_EVENT, {
        detail: { page: Pages.SignUp, fromUpgrade: true },
      }));
      return;
    }
    markUpgradeEntitlementRefreshPending();
    onUpgrade?.();
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm"
      data-testid="tid.commercial.upgrade-backdrop"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <section
        aria-describedby="commercial-upgrade-description"
        aria-labelledby="commercial-upgrade-title"
        aria-modal="true"
        className="relative grid w-full max-w-[860px] overflow-hidden rounded-3xl border border-white/70 bg-[#f7f5ef] shadow-[0_32px_100px_-24px_rgba(15,23,42,0.55)] md:grid-cols-[1.05fr_0.95fr]"
        data-testid="tid.commercial.upgrade-dialog"
        ref={dialogRef}
        role="dialog"
      >
        <button
          aria-label="Close upgrade dialog"
          className="absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-full border border-slate-300/80 bg-white/80 text-xl leading-none text-slate-600 transition hover:border-slate-400 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
          onClick={onClose}
          type="button"
        >
          <span aria-hidden="true">×</span>
        </button>

        <div className="flex min-h-[530px] flex-col px-7 py-8 sm:px-10 sm:py-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">
            {presentation.eyebrow}
          </p>
          <h2
            className="mt-4 max-w-md text-3xl font-semibold leading-[1.08] tracking-[-0.035em] text-slate-950 sm:text-4xl"
            id="commercial-upgrade-title"
          >
            {presentation.title}
          </h2>
          <p
            className="mt-4 max-w-md text-[15px] leading-6 text-slate-600"
            id="commercial-upgrade-description"
          >
            {presentation.description}
          </p>

          <ul className="mt-7 space-y-3" aria-label={`${presentation.targetPlan} includes`}>
            {presentation.benefits.map((benefit) => (
              <li className="flex items-start gap-3 text-sm text-slate-800" key={benefit}>
                <span
                  aria-hidden="true"
                  className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-amber-200 text-xs font-bold text-amber-950"
                >
                  ✓
                </span>
                <span>{benefit}</span>
              </li>
            ))}
          </ul>

          <div className="mt-auto pt-8">
            <div className="flex flex-wrap items-end justify-between gap-3 border-t border-slate-300/80 pt-6">
              <div>
                <p className="text-lg font-semibold tracking-tight text-slate-950">{presentation.price}</p>
                <p className="mt-1 text-xs text-slate-500">{presentation.priceDetail}</p>
              </div>
              <a
                className="inline-flex min-h-11 items-center justify-center rounded-full bg-slate-950 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-950/15 transition hover:-translate-y-0.5 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
                data-testid="tid.commercial.upgrade-primary"
                href={presentation.href}
                onClick={handleUpgrade}
                ref={primaryActionRef}
              >
                {presentation.ctaLabel}
                <span aria-hidden="true" className="ml-2">↗</span>
              </a>
            </div>
          </div>
        </div>

        <OutcomeTaste presentation={presentation} />
      </section>
    </div>
  );
}

function OutcomeTaste({ presentation }: { presentation: CommercialUpgradePresentation }) {
  const rows = presentation.targetPlan === 'enterprise'
    ? [
        ['Priority signal', 'Ready'],
        ['Decision context', 'Connected'],
        ['Next action', 'Assigned'],
      ]
    : presentation.targetPlan === 'business'
      ? [
          ['People source', 'Live'],
          ['Process source', 'Live'],
          ['Controls source', 'Live'],
        ]
      : [
          ['Your first source', 'Live'],
          ['Refresh', 'Every 24h'],
          ['Canvas', 'Unlimited'],
        ];

  return (
    <aside className="relative flex min-h-[400px] flex-col overflow-hidden bg-slate-950 px-7 pb-8 pt-16 text-white sm:px-9 md:min-h-[530px]">
      <div aria-hidden="true" className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-amber-400/20 blur-3xl" />
      <div aria-hidden="true" className="absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-teal-400/10 blur-3xl" />
      <div className="relative">
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300">A taste of the outcome</p>
        <h3 className="mt-4 text-2xl font-semibold leading-tight tracking-[-0.025em]">
          {presentation.outcomeTitle}
        </h3>
        <p className="mt-3 text-sm leading-6 text-slate-300">{presentation.outcomeDescription}</p>
      </div>

      <div className="relative mt-8 rounded-2xl border border-white/10 bg-white/[0.06] p-4 shadow-2xl backdrop-blur">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <span className="text-xs font-semibold text-slate-200">Illustrative outcome</span>
          <span className="rounded-full bg-amber-300/15 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-200">Synthetic</span>
        </div>
        <dl className="mt-1 divide-y divide-white/10">
          {rows.map(([label, value]) => (
            <div className="flex items-center justify-between py-3" key={label}>
              <dt className="text-xs text-slate-400">{label}</dt>
              <dd className="text-sm font-semibold text-white">{value}</dd>
            </div>
          ))}
        </dl>
      </div>

      <p className="relative mt-auto pt-6 text-xs leading-5 text-slate-400">
        Example data only. Your workspace data is never loaded into this preview.
      </p>
    </aside>
  );
}
